import { useState, type InputHTMLAttributes } from "react";
import { useApp } from "../../context/AppContext";
import type { LockPhase, PersonalMatch } from "../../models/types";
import { formatUsDateInput } from "../../utils/birthDate";
import { personalIntroChecks, personalRemainingChecks } from "../../utils/calendar";
function AppMark() {
  return (
    <div className="hero-icon" aria-hidden>
      <img
        src={`${import.meta.env.BASE_URL}icons/icon-192.png`}
        alt=""
        width={88}
        height={88}
        draggable={false}
      />
    </div>
  );
}

function ErrorBlock({ message }: { message: string | null }) {
  if (!message) return null;
  return <p className="error-text">{message}</p>;
}

function LabeledField({
  id,
  label,
  value,
  onChange,
  placeholder,
  autoFocus,
  type = "text",
  inputMode,
  autoComplete,
  maxLength,
  enterKeyHint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  type?: string;
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
  autoComplete?: string;
  maxLength?: number;
  enterKeyHint?: InputHTMLAttributes<HTMLInputElement>["enterKeyHint"];
}) {
  return (
    <label className="field-group" htmlFor={id}>
      <span className="field-label">{label}</span>
      <input
        id={id}
        className="field"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        inputMode={inputMode}
        autoComplete={autoComplete}
        maxLength={maxLength}
        enterKeyHint={enterKeyHint}
      />
    </label>
  );
}

function WelcomeScreen() {
  const app = useApp();
  return (
    <div className="center-col">
      <div className="spacer" />
      <AppMark />
      <h1 className="lock-title">Welcome to Sundius Family</h1>
      <p className="lock-body">
        The private app for the Sundius family tree, photos, and games.
      </p>
      <ErrorBlock message={app.lastError} />
      {app.pendingAccessRequestId && (
        <div className="card pad-screen stack">
          <strong>Access request sent</strong>
          <p className="muted">
            Suzy or Kyle still need to approve you. You can leave this screen — we&apos;ll keep your request.
          </p>
          <button type="button" className="btn-primary" onClick={() => app.showPendingAccessRequest()}>
            Check request status
          </button>
        </div>
      )}
      <button type="button" className="btn-primary" onClick={() => app.showPersonalNameEntry()}>
        I&apos;m a family member
      </button>
      <button type="button" className="btn-ghost" onClick={() => app.showInviteEntry()}>
        I have a verification code
      </button>
      <button type="button" className="btn-ghost" onClick={() => app.showAccessHelp()}>
        I need help getting in
      </button>
      <div className="spacer" />
    </div>
  );
}

function PersonalNameEntry() {
  const app = useApp();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  return (
    <div className="center-col">
      <div className="spacer" />
      <AppMark />
      <h1 className="title-serif" style={{ textAlign: "center" }}>Who are you?</h1>
      <p className="muted" style={{ textAlign: "center" }}>
        Enter your name as the family knows you.
      </p>
      <LabeledField
        id="personal-first-name"
        label="First name"
        value={firstName}
        onChange={setFirstName}
        placeholder="e.g. Mike"
        autoFocus
        autoComplete="given-name"
      />
      <LabeledField
        id="personal-last-name"
        label="Last name"
        value={lastName}
        onChange={setLastName}
        placeholder="e.g. Sundius"
        autoComplete="family-name"
      />
      <ErrorBlock message={app.lastError} />
      <button
        type="button"
        className="btn-primary"
        disabled={!firstName.trim() || !lastName.trim() || app.isWorking}
        onClick={() => void app.matchPersonalName(firstName, lastName)}
      >
        {app.isWorking ? "Matching…" : "Continue"}
      </button>
      <button type="button" className="btn-ghost" onClick={() => app.showWelcome()}>Back</button>
      <div className="spacer" />
    </div>
  );
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ""}${parts[parts.length - 1]![0] ?? ""}`.toUpperCase();
  }
  return (parts[0]?.slice(0, 2) ?? "?").toUpperCase();
}

function PersonalAvatar({
  displayName,
  avatarB64,
  size = 180,
}: {
  displayName: string;
  avatarB64: string | null;
  size?: number;
}) {
  if (avatarB64) {
    return (
      <img
        className="avatar avatar-ring"
        src={`data:image/jpeg;base64,${avatarB64}`}
        alt={displayName}
        width={size}
        height={size}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="avatar avatar-ring photo-fallback"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.28) }}
      aria-label={displayName}
      role="img"
    >
      {initialsFromName(displayName)}
    </div>
  );
}

function PersonalWelcome({ match }: { match: PersonalMatch }) {
  const app = useApp();
  return (
    <div className="center-col">
      <div className="spacer" />
      <PersonalAvatar
        displayName={match.display_name}
        avatarB64={app.personalAvatarB64 ?? match.avatar_b64 ?? null}
        size={180}
      />
      <h1 className="title-serif" style={{ textAlign: "center" }}>
        Welcome, {match.display_name}!
      </h1>
      <p className="muted" style={{ textAlign: "center" }}>
        {match.already_enrolled
          ? "Welcome back — continue to verify it's you and sign in on this browser."
          : "It's great to have you here."}
      </p>
      <button type="button" className="btn-primary" onClick={() => app.continuePersonalWelcome(match)}>
        Continue
      </button>
      {match.already_enrolled ? (
        <button type="button" className="btn-ghost" onClick={() => app.showInviteEntry()}>
          Use an invitation code instead
        </button>
      ) : null}
      <button type="button" className="btn-ghost" onClick={() => app.showWelcome()}>Back</button>
      <div className="spacer" />
    </div>
  );
}

function PersonalVerifyIntro({ match }: { match: PersonalMatch }) {
  const app = useApp();
  const [a, b] = personalIntroChecks(match.challenge_path);
  return (
    <div className="center-col">
      <div className="spacer" />
      <PersonalAvatar
        displayName={match.display_name}
        avatarB64={app.personalAvatarB64 ?? match.avatar_b64 ?? null}
        size={120}
      />
      <h1 className="title-serif" style={{ textAlign: "center" }}>Quick verification</h1>
      <p className="muted" style={{ textAlign: "center" }}>
        Signing in as <strong>{match.display_name}</strong>. We&apos;ll ask for{" "}
        {personalRemainingChecks(match.challenge_path)} detail
        {personalRemainingChecks(match.challenge_path) === 1 ? "" : "s"} — starting with your birthday.
      </p>
      <div className="card pad-screen stack">
        <div>1. {a}</div>
        {b ? <div>2. {b}</div> : null}
      </div>
      <button type="button" className="btn-primary" onClick={() => app.continuePersonalVerifyIntro(match)}>
        Start
      </button>
      <div className="spacer" />
    </div>
  );
}

function PersonalUnlockHelpSection({
  match,
  reason,
}: {
  match: PersonalMatch;
  reason: string;
}) {
  const app = useApp();
  if (!app.showPersonalUnlockHelp) return null;
  return (
    <div className="stack" style={{ width: "100%", marginTop: 8 }}>
      <p className="muted" style={{ textAlign: "center", margin: 0 }}>
        We&apos;ll notify Kyle. You can keep trying, or wait for approval.
      </p>
      <button
        type="button"
        className="btn-ghost"
        disabled={app.isWorking}
        onClick={() => void app.submitPersonalUnlockHelp(match, reason)}
      >
        {app.isWorking ? "Sending request…" : "Request help from an admin"}
      </button>
    </div>
  );
}

function StepForm({
  title,
  hint,
  label,
  placeholder,
  onSubmit,
  onBack,
  inputMode,
  autoComplete,
  formatValue,
  maxLength,
  help,
}: {
  title: string;
  hint: string;
  label: string;
  placeholder?: string;
  onSubmit: (value: string) => void;
  onBack?: () => void;
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
  autoComplete?: string;
  formatValue?: (raw: string) => string;
  maxLength?: number;
  help?: React.ReactNode;
}) {
  const app = useApp();
  const [value, setValue] = useState("");
  const fieldId = `step-${label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div className="center-col">
      <div className="spacer" />
      <h1 className="title-serif" style={{ textAlign: "center" }}>{title}</h1>
      <p className="muted" style={{ textAlign: "center" }}>{hint}</p>
      <LabeledField
        id={fieldId}
        label={label}
        value={value}
        onChange={(next) => setValue(formatValue ? formatValue(next) : next)}
        placeholder={placeholder}
        autoFocus
        inputMode={inputMode}
        autoComplete={autoComplete}
        maxLength={maxLength}
        enterKeyHint="done"
      />
      <ErrorBlock message={app.lastError} />
      {help}
      <button type="button" className="btn-primary" disabled={!value.trim() || app.isWorking} onClick={() => onSubmit(value)}>
        {app.isWorking ? "Checking…" : "Continue"}
      </button>
      {onBack ? (
        <button type="button" className="btn-ghost" onClick={onBack}>Back</button>
      ) : null}
      <div className="spacer" />
    </div>
  );
}

function PersonalBirthdayStep({ match }: { match: PersonalMatch }) {
  const app = useApp();
  return (
    <StepForm
      title="Your birthday"
      hint="Just type the numbers — we'll add the slashes."
      label="Birthday"
      placeholder="MM/DD/YYYY"
      inputMode="numeric"
      autoComplete="bday"
      formatValue={formatUsDateInput}
      maxLength={10}
      onSubmit={(v) => void app.submitPersonalBirthday(match, v)}
      onBack={() => app.showWelcome()}
      help={<PersonalUnlockHelpSection match={match} reason="personal_auth_birthday" />}
    />
  );
}

function PersonalPartnerStep({ match }: { match: PersonalMatch }) {
  const app = useApp();
  return (
    <StepForm
      title={match.partner_prompt_copy ?? "Your partner"}
      hint="Enter their goes-by name."
      label="Partner name"
      placeholder="Goes-by name"
      autoComplete="off"
      onSubmit={(v) => void app.submitPersonalPartner(match, v)}
      help={<PersonalUnlockHelpSection match={match} reason="personal_auth_partner" />}
    />
  );
}

function PersonalPhoneStep({ match }: { match: PersonalMatch }) {
  const app = useApp();
  return (
    <StepForm
      title="Your mobile number"
      hint="Digits only is fine."
      label="Mobile number"
      placeholder="Phone number"
      inputMode="tel"
      autoComplete="tel"
      onSubmit={(v) => void app.submitPersonalPhone(match, v)}
      help={<PersonalUnlockHelpSection match={match} reason="personal_auth_phone" />}
    />
  );
}

function AlreadyRegisteredStep({ displayName }: { displayName?: string }) {
  const app = useApp();
  return (
    <div className="center-col">
      <div className="spacer" />
      <AppMark />
      <h1 className="title-serif" style={{ textAlign: "center" }}>
        Already signed in elsewhere
      </h1>
      <p className="muted" style={{ textAlign: "center" }}>
        {displayName ? (
          <>
            <strong>{displayName}</strong> is already signed in on another phone or
            browser.
          </>
        ) : (
          <>You&apos;re already signed in on another phone or browser.</>
        )}
      </p>
      <div className="card pad-screen stack">
        <strong>Get a verification code from that device</strong>
        <p className="muted" style={{ margin: 0 }}>
          On the phone that&apos;s already in, open <strong>Send the app</strong> →{" "}
          <strong>My other phone or iPad</strong>, then enter that code here.
        </p>
      </div>
      <button type="button" className="btn-primary" onClick={() => app.showInviteEntry()}>
        Enter verification code
      </button>
      <button type="button" className="btn-ghost" onClick={() => app.showWelcome()}>
        Back
      </button>
      <div className="spacer" />
    </div>
  );
}

function PersonalStateStep({ match }: { match: PersonalMatch }) {
  const app = useApp();
  return (
    <StepForm
      title="Your state"
      hint="Where you live now."
      label="State"
      placeholder="e.g. Maryland"
      autoComplete="address-level1"
      onSubmit={(v) => void app.submitPersonalState(match, v)}
      help={<PersonalUnlockHelpSection match={match} reason="personal_auth_state" />}
    />
  );
}

function PersonalWeddingStep({ match }: { match: PersonalMatch }) {
  const app = useApp();
  return (
    <StepForm
      title="Your wedding date"
      hint="Just type the numbers — we'll add the slashes."
      label="Wedding date"
      placeholder="MM/DD/YYYY"
      inputMode="numeric"
      formatValue={formatUsDateInput}
      maxLength={10}
      onSubmit={(v) => void app.submitPersonalWedding(match, v)}
      help={<PersonalUnlockHelpSection match={match} reason="personal_auth_wedding" />}
    />
  );
}

function InviteEntry() {
  const app = useApp();
  const [code, setCode] = useState("");
  const [inspected, setInspected] = useState<{
    display_name: string;
    person_id: string;
    invite_kind: string;
    relationship_note?: string | null;
  } | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  if (inspected) {
    return (
      <div className="center-col">
        <div className="spacer" />
        <h1 className="title-serif" style={{ textAlign: "center" }}>Confirm it&apos;s you</h1>
        <p style={{ textAlign: "center" }}>
          This code is for <strong>{inspected.display_name}</strong>.
        </p>
        {inspected.relationship_note ? (
          <p className="muted" style={{ textAlign: "center" }}>{inspected.relationship_note}</p>
        ) : null}
        <ErrorBlock message={app.lastError ?? localError} />
        <button type="button" className="btn-primary" disabled={app.isWorking} onClick={() => void app.redeemInviteCode(code)}>
          {app.isWorking ? "Unlocking…" : "Unlock"}
        </button>
        <button type="button" className="btn-ghost" onClick={() => setInspected(null)}>Back</button>
        <div className="spacer" />
      </div>
    );
  }

  return (
    <div className="center-col">
      <div className="spacer" />
      <AppMark />
      <h1 className="title-serif" style={{ textAlign: "center" }}>Sundius Family</h1>
      <p className="muted" style={{ textAlign: "center" }}>
        Enter your personal invite code. One-time setup on this browser.
      </p>
      <LabeledField
        id="invite-code"
        label="Invite code"
        value={code}
        onChange={setCode}
        placeholder="sund_xxxx-xxxx"
        autoFocus
        autoComplete="one-time-code"
      />
      <ErrorBlock message={app.lastError ?? localError} />
      {app.personalUnlockHelpMatch && app.showPersonalUnlockHelp ? (
        <PersonalUnlockHelpSection
          match={app.personalUnlockHelpMatch}
          reason="personal_auth_birthday"
        />
      ) : null}
      <button
        type="button"
        className="btn-primary"
        disabled={!code.trim() || app.isWorking}
        onClick={() => {
          setLocalError(null);
          void app.inspectInviteCode(code).then(setInspected).catch((e: Error) => setLocalError(e.message));
        }}
      >
        {app.isWorking ? "Checking…" : "Continue"}
      </button>
      <button type="button" className="btn-ghost" onClick={() => app.showWelcome()}>Back</button>
      <div className="spacer" />
    </div>
  );
}

function AccessHelp() {
  const app = useApp();
  return (
    <div className="center-col">
      <div className="spacer" />
      <AppMark />
      <h1 className="title-serif" style={{ textAlign: "center" }}>Need access?</h1>
      <p className="muted" style={{ textAlign: "center" }}>
        Ask Kyle or Suzy for an invite code, or send an access request.
      </p>
      <button type="button" className="btn-primary" onClick={() => app.showAccessRequestForm()}>
        Request access
      </button>
      <button type="button" className="btn-ghost" onClick={() => app.showWelcome()}>Back</button>
      <div className="spacer" />
    </div>
  );
}

function AccessRequestForm() {
  const app = useApp();
  const [requesterName, setRequesterName] = useState("");
  const [sharerName, setSharerName] = useState("");
  const [locationStatus, setLocationStatus] = useState("Location required");
  const [capturedLocation, setCapturedLocation] = useState<{
    latitude: number;
    longitude: number;
    accuracy_m: number | null;
  } | null>(null);
  const [isCapturingLocation, setIsCapturingLocation] = useState(false);

  const captureLocation = () => {
    if (!("geolocation" in navigator)) {
      setLocationStatus("Location is not available in this browser.");
      return;
    }
    setIsCapturingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCapturedLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy ?? null,
        });
        setLocationStatus(
          `Location captured (${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)})`,
        );
        setIsCapturingLocation(false);
      },
      (err) => {
        setCapturedLocation(null);
        setLocationStatus(err.message || "Could not get location.");
        setIsCapturingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const canSubmit =
    requesterName.trim().length >= 2 &&
    sharerName.trim().length >= 2 &&
    capturedLocation != null;

  return (
    <div className="center-col">
      <h1 className="title-serif" style={{ textAlign: "center" }}>Request family access</h1>
      <p className="muted" style={{ textAlign: "center" }}>
        Tell us who you are and who shared the app. Your location helps the admin verify the request.
      </p>
      <LabeledField
        id="access-requester-name"
        label="Your name"
        value={requesterName}
        onChange={setRequesterName}
        placeholder="Full name"
        autoComplete="name"
      />
      <LabeledField
        id="access-sharer-name"
        label="Who sent you the app?"
        value={sharerName}
        onChange={setSharerName}
        placeholder="Family member’s name"
        autoComplete="off"
      />
      <p className="muted">{locationStatus}</p>
      <button type="button" className="btn-primary" disabled={isCapturingLocation} onClick={captureLocation}>
        {isCapturingLocation ? "Getting location…" : "Use current location"}
      </button>
      <ErrorBlock message={app.lastError} />
      <button
        type="button"
        className="btn-primary"
        disabled={!canSubmit || app.isWorking}
        onClick={() => {
          if (!capturedLocation) return;
          void app.submitAccessRequest({
            requester_name: requesterName.trim(),
            sharer_name: sharerName.trim(),
            latitude: capturedLocation.latitude,
            longitude: capturedLocation.longitude,
            accuracy_m: capturedLocation.accuracy_m,
          });
        }}
      >
        Send request
      </button>
      <button type="button" className="btn-ghost" onClick={() => app.showAccessHelp()}>Back</button>
    </div>
  );
}

function AccessRequestPending({ requestId }: { requestId: string }) {
  const app = useApp();
  return (
    <div className="center-col">
      <div className="spacer" />
      <h1 className="title-serif" style={{ textAlign: "center" }}>Waiting for approval</h1>
      <p className="muted" style={{ textAlign: "center" }}>
        Request {requestId.slice(0, 8)}… is pending. Kyle or Suzy will review it.
      </p>
      <ErrorBlock message={app.lastError} />
      <button type="button" className="btn-primary" disabled={app.isWorking} onClick={() => void app.pollAccessRequest()}>
        Check status
      </button>
      <button type="button" className="btn-ghost" onClick={() => app.showWelcome()}>Back</button>
      <div className="spacer" />
    </div>
  );
}

function DeviceResume({ displayName }: { displayName: string }) {
  const app = useApp();
  return (
    <div className="center-col">
      <div className="spacer" />
      <AppMark />
      <h1 className="title-serif" style={{ textAlign: "center" }}>Welcome back, {displayName}</h1>
      <p className="muted" style={{ textAlign: "center" }}>
        This browser was already set up for Sundius Family.
      </p>
      <ErrorBlock message={app.lastError} />
      <button type="button" className="btn-primary" disabled={app.isWorking} onClick={() => void app.resumeEnrolledDevice()}>
        Continue
      </button>
      <button
        type="button"
        className="btn-ghost"
        disabled={app.isWorking}
        onClick={() => void app.abandonEnrolledDevice()}
      >
        Use a different code
      </button>
      <div className="spacer" />
    </div>
  );
}

function ReturningWelcome({ displayName }: { displayName: string }) {
  const app = useApp();
  return (
    <div className="center-col">
      <div className="spacer" />
      <AppMark />
      <h1 className="title-serif" style={{ textAlign: "center" }}>Welcome back, {displayName}</h1>
      <ErrorBlock message={app.lastError} />
      <button type="button" className="btn-primary" disabled={app.isWorking} onClick={() => void app.continueIntoApp()}>
        Continue
      </button>
      <button type="button" className="btn-ghost" disabled={app.isWorking} onClick={() => void app.signOut()}>
        Sign out
      </button>
      <div className="spacer" />
    </div>
  );
}

function CheckingEnrollment() {
  return (
    <div className="center-col">
      <div className="spacer" />
      <AppMark />
      <p className="muted" style={{ textAlign: "center" }}>Checking enrollment…</p>
      <div className="spacer" />
    </div>
  );
}

function PhaseBody({ phase }: { phase: LockPhase }) {
  switch (phase.kind) {
    case "checkingEnrollment":
      return <CheckingEnrollment />;
    case "welcome":
      return <WelcomeScreen />;
    case "personalNameEntry":
      return <PersonalNameEntry />;
    case "personalWelcome":
      return <PersonalWelcome match={phase.match} />;
    case "personalVerifyIntro":
      return <PersonalVerifyIntro match={phase.match} />;
    case "personalBirthday":
      return <PersonalBirthdayStep match={phase.match} />;
    case "personalPartner":
      return <PersonalPartnerStep match={phase.match} />;
    case "personalPhone":
      return <PersonalPhoneStep match={phase.match} />;
    case "personalState":
      return <PersonalStateStep match={phase.match} />;
    case "personalWedding":
      return <PersonalWeddingStep match={phase.match} />;
    case "alreadyRegistered":
      return <AlreadyRegisteredStep displayName={phase.displayName} />;
    case "inviteEntry":
      return <InviteEntry />;
    case "accessHelp":
      return <AccessHelp />;
    case "accessRequestForm":
      return <AccessRequestForm />;
    case "accessRequestPending":
      return <AccessRequestPending requestId={phase.requestId} />;
    case "deviceResume":
      return <DeviceResume displayName={phase.displayName} />;
    case "returningWelcome":
      return <ReturningWelcome displayName={phase.displayName} />;
    default:
      return null;
  }
}

export function LockGate() {
  const { phase } = useApp();
  return (
    <div className="screen-lock">
      <PhaseBody phase={phase} />
    </div>
  );
}
